import { isFilled, type PositionsView } from '../crew/positions';
import { MONTHS_SHORT } from './peopleGrid';

/**
 * „Dostępność → Ludzie”: staffing from the grid (owner 2026-10-25 16:56–17:02, 2026-10-26 07:49) — what the events strip
 * and the panel show, without React.
 */

/** Owner 2026-10-26 07:49: the events strip above the people — per function the filled places against the needed ones. */
export type StripFunction = { functionId: string | null; functionName: string; needed: number; filled: number; confirmed?: number };
/** No `eventId`: an event the user may not see („inne wydarzenie”); no `functions`: no numbers to show. */
export type StripEvent = { eventId?: string; eventName: string; firstDay: string; lastDay: string; complete?: boolean; functions?: StripFunction[] };
export type Where = { eventId?: string; eventName: string; functionName?: string; hours?: string };
export type Candidate = { personId: string; name: string; confirmed?: true; where?: Where[] };
export type PanelPlace = {
  eventId: string;
  eventName: string;
  confirmed: boolean;
  positionId?: string;
  functionId: string | null;
  functionName: string;
  /** „Inni” (filled by hand only). */
  others?: boolean;
  source: 'manual' | 'rule';
  label: string;
  description?: string;
  groups: { free: Candidate[]; staffed: Candidate[]; unavailable: Candidate[] };
};
export type Panel = { day: string; person?: { id: string; name?: string }; places: PanelPlace[]; namesHidden?: true };
export type GridFillResult = { eventId: string; positionId?: string; assignmentId: string; day: string; otherDays: string[]; collisions: Array<{ day: string; events: Array<{ eventId?: string; eventName: string }> }> };

/** The person panel of „Obsadź…” (`…/open-day`) — under the grid's key, so `refreshCrew` refreshes it. */
export const OPEN_KEY = ['crew', 'people-availability', 'open'] as const;

/** A bar's numbers, in its hover bubble (owner 2026-10-27 08:16 — not in the bar): „Realizator 1/1, Technik 2/3”. */
export const barText = (e: Pick<StripEvent, 'functions'>): string => (e.functions ?? []).map((f) => `${f.functionName} ${f.filled}/${f.needed}`).join(', ');

/**
 * Owner 2026-10-27 08:21–08:22: the „Brakuje” row — per day the free places of the strip's events (rule 7a: „inne
 * wydarzenie”); `functions` (owner 12:27): what is missing per function — absent without the field „Funkcja” (rule 7a).
 */
export type MissingDay = { day: string; total: number; events: Array<{ eventId?: string; eventName: string; missing: number; functions?: Array<{ functionName: string; missing: number }> }> };
/**
 * The bubble of a day's number, one event per line: which functions are missing and how many — „Wesele A: Technik 2,
 * Realizator 1” (owner 12:27, the server's order: the global one of #200); without „Funkcja” only how many — „Wesele A: 2”.
 */
export const missingLines = (d: MissingDay): string[] => d.events.map((e) => `${e.eventName}: ${e.functions && e.functions.length > 0 ? e.functions.map((f) => `${f.functionName} ${f.missing}`).join(', ') : e.missing}`);
/** Green when every place is filled and confirmed, yellow otherwise, neutral without numbers. */
export const barTone = (e: Pick<StripEvent, 'functions' | 'complete'>): 'ok' | 'missing' | 'unknown' => (!e.functions ? 'unknown' : e.complete ? 'ok' : 'missing');
export const crewLink = (eventId: string): string => `/wydarzenia?wydarzenie=${encodeURIComponent(eventId)}&zakladka=obsada`;

/** Where a bar sits among the visible days: the columns before it, its span, the columns after; null when outside. */
export function barColumns(e: Pick<StripEvent, 'firstDay' | 'lastDay'>, days: readonly string[]): { before: number; span: number; after: number } | null {
  const start = days.findIndex((d) => d >= e.firstDay);
  if (start < 0 || days[start]! > e.lastDay) return null;
  let end = start;
  while (end + 1 < days.length && days[end + 1]! <= e.lastDay) end += 1;
  return { before: start, span: end - start + 1, after: days.length - end - 1 };
}

/** Owner 2026-10-27 23:31: the share (0–1) of a bar's visible days that are before `today` — its greyed part. */
export function barPast(cols: { before: number; span: number }, days: readonly string[], today: string): number {
  return days.slice(cols.before, cols.before + cols.span).filter((d) => d < today).length / cols.span;
}

/**
 * The strip's rows (owner 2026-10-27 21:04: „nie muszą być każde w osobnym wierszu”): the bars sorted by their first
 * visible day, then the longer first; each goes into the first row where it overlaps no bar on any visible day — a new
 * row only for an overlap. Every kind of bar the strip shows goes through here.
 */
export function packBars<T extends { cols: { before: number; span: number } }>(bars: readonly T[]): T[][] {
  const sorted = bars
    .map((b, i) => ({ b, i }))
    .sort((x, y) => x.b.cols.before - y.b.cols.before || y.b.cols.span - x.b.cols.span || x.i - y.i)
    .map((x) => x.b);
  const rows: T[][] = [];
  const ends: number[] = []; // per row: the first free column
  for (const bar of sorted) {
    const at = ends.findIndex((end) => end <= bar.cols.before);
    if (at < 0) {
      rows.push([bar]);
      ends.push(bar.cols.before + bar.cols.span);
    } else {
      rows[at]!.push(bar);
      ends[at] = bar.cols.before + bar.cols.span;
    }
  }
  return rows;
}

/** A place in the panel: „Wesele A — Technik, miejsce 2” (an „Inni” place with its description). */
export const placeTitle = (p: Pick<PanelPlace, 'eventName' | 'label' | 'description'>): string => `${p.eventName} — ${p.label.replace(' — miejsce', ', miejsce')}${p.description ? ` (${p.description})` : ''}`;

/** Where a person already works that day: „Koncert B — Realizator, 18:00–23:00”. */
export const whereText = (list: readonly Where[] | undefined): string =>
  (list ?? []).map((w) => `${w.eventName}${w.functionName ? ` — ${w.functionName}` : ''}${w.hours ? `${w.functionName ? ',' : ' —'} ${w.hours}` : ''}`).join('; ');

/** After a fill: „Kolizja 7.11: także „Koncert B”” per day, or nothing. */
export const collisionText = (r: Pick<GridFillResult, 'collisions'>, short: (day: string) => string): string =>
  r.collisions.map((c) => `Kolizja ${short(c.day)}: także ${c.events.map((e) => `„${e.eventName}”`).join(', ')}`).join('. ');

/** The days „Wymuś” takes after a fill (review 3): every day of its collisions — a rule place covers the whole event. */
export const forceDays = (r: Pick<GridFillResult, 'collisions'>): string[] => [...new Set(r.collisions.map((c) => c.day))].sort();

/** The side panel's „Brakuje: Technik 1 · Inni 2” — the places a bar is short of (its worst day); '' when none. */
export const missingText = (e: Pick<StripEvent, 'functions'>): string =>
  (e.functions ?? [])
    .filter((f) => f.needed > f.filled)
    .map((f) => `${f.functionName} ${f.needed - f.filled}`)
    .join(' · ');

/** The side panel's „Kolizja: N” — the days of the crew in a collision (one per person and day). */
export const collisionCount = (crew: ReadonlyArray<{ days?: ReadonlyArray<{ collision?: readonly unknown[] }> }>): number =>
  crew.reduce((n, a) => n + (a.days ?? []).filter((d) => (d.collision ?? []).length > 0).length, 0);

/** The side panel's dates: „31 paź – 1 lis”, „7 – 9 lis”, „7 lis”. */
export function eventDates(e: Pick<StripEvent, 'firstDay' | 'lastDay'>): string {
  const d = (day: string) => Number(day.slice(8));
  const m = (day: string) => MONTHS_SHORT[Number(day.slice(5, 7)) - 1]!;
  if (e.firstDay === e.lastDay) return `${d(e.firstDay)} ${m(e.firstDay)}`;
  if (e.firstDay.slice(0, 7) === e.lastDay.slice(0, 7)) return `${d(e.firstDay)} – ${d(e.lastDay)} ${m(e.lastDay)}`;
  return `${d(e.firstDay)} ${m(e.firstDay)} – ${d(e.lastDay)} ${m(e.lastDay)}`;
}

/**
 * Review M2: the side panel's „Brakuje: Technik 2” from the event's own places (the panel is loaded by the event's id,
 * also when a filter hides its bar) — per function the empty places of its worst day, in the places' order.
 */
export function missingFromView(view: PositionsView): string {
  const worst = new Map<string, { name: string; empty: number }>();
  for (const d of view.days) {
    for (const g of d.groups) {
      const empty = g.places.filter((p) => !isFilled(p)).length;
      const now = worst.get(g.functionId) ?? { name: g.functionName, empty: 0 };
      worst.set(g.functionId, { name: now.name, empty: Math.max(now.empty, empty) });
    }
  }
  return [...worst.values()].filter((f) => f.empty > 0).map((f) => `${f.name} ${f.empty}`).join(' · ');
}
